import {useAppDispatch, useAppSelector} from "../../../app/store";
import {ADMIN_LANG} from "../../../settings";
import {VersionSection, VersionSectionContent, VersionSectionContentLanguage} from "../../../types/admin";
import {openSectionModal} from "../../../app/slices/modals/sectionEditModal";
import {openQuestionModal} from "../../../app/slices/modals/questionEditModal";
import {DragDropContext, Draggable, Droppable, DropResult} from "@hello-pangea/dnd";
import {changeGameDetail, updateSectionOrder} from "../../../app/slices/adminGameView";
import {Accordion, Button, Col, Row} from "react-bootstrap";
import {
    BarChartLine,
    ExclamationTriangleFill, Eye,
    Gear,
    GripVertical,
    Plus
} from "react-bootstrap-icons";
import {useNavigate} from "react-router-dom";
import {openPreview} from "../../../app/slices/offCanvas/previewOffCanvas";
import {openSectionOrderModal} from "../../../app/slices/modals/sectionOrderModal";

type SectionDetailsProps = {
    language: string;
    addNewSection: () => void;
}

const SectionDetails = ({language, addNewSection}: SectionDetailsProps) => {
    const dispatch = useAppDispatch();
    const {
        version_sections,
        is_draft,
        is_published,
    } = useAppSelector((state) => state.adminGameView);

    const handleOpenSectionOrder = () => {
        dispatch(openSectionOrderModal({language}));
    }

    return (
        <Row className='gy-4'>
            <Col xs={12}>
                <h3>{ADMIN_LANG.game_sections_and_contents}</h3>
                {is_draft &&
                    <>
                        {version_sections.length > 2 &&
                            <Button variant='primary' onClick={handleOpenSectionOrder}
                                    className='me-2'>{ADMIN_LANG.edit_order}</Button>
                        }
                        <Button variant='success' onClick={addNewSection}>
                            <Plus className='me-1'/>
                            {ADMIN_LANG.new_section}</Button>
                    </>
                }
                {is_published &&
                    <p className='text-info fw-semibold'>
                        <ExclamationTriangleFill className="me-2"/>
                        <span dangerouslySetInnerHTML={{__html: ADMIN_LANG.published_info}}></span>
                    </p>
                }
            </Col>
            <Col xs={12}>
                {version_sections && <SectionList language={language}/>}
            </Col>
        </Row>
    );
}

export default SectionDetails;

type SectionListProps = {
    language: string;
}

const SectionList = ({language}: SectionListProps) => {
    const {
        version_sections,
        is_draft,
        is_published,
        game_id,
        current_version_id
    } = useAppSelector((state) => state.adminGameView);
    const dispatch = useAppDispatch();

    const handleEditSection = (section_id: string) => {
        dispatch(openSectionModal({section_id, language}));
    }


    const handleAddNewContent = (section_id: string) => {
        dispatch(openQuestionModal({
            section_id: section_id,
            content_id: null,
            language: language,
            type: ''
        }));
    }

    const handleDragEnd = (result: DropResult) => {
        if (!is_draft) return;
        const {source, destination} = result;

        if (!destination) return;
        const sourceSection = version_sections.find((section) => section.key === source.droppableId);
        const destinationSection = version_sections.find((section) => section.key === destination.droppableId);

        if (!sourceSection || !destinationSection) return;
        let updatedSections: VersionSection[];

        if (source.droppableId === destination.droppableId) {
            const newContents = Array.from(sourceSection.contents);
            const [removed] = newContents.splice(source.index, 1);
            newContents.splice(destination.index, 0, removed);
            updatedSections = version_sections.map((section) => {
                if (section.key === source.droppableId) {
                    return {
                        ...section,
                        contents: newContents
                    };
                }
                return section;
            });
        } else {
            const sourceContents = Array.from(sourceSection.contents);
            const destinationContents = Array.from(destinationSection.contents);
            const [removed] = sourceContents.splice(source.index, 1);
            destinationContents.splice(destination.index, 0, removed);
            updatedSections = version_sections.map((section) => {
                if (section.key === source.droppableId) {
                    return {
                        ...section,
                        contents: sourceContents
                    };
                }
                if (section.key === destination.droppableId) {
                    return {
                        ...section,
                        contents: destinationContents
                    };
                }
                return section;
            });
        }
        dispatch(changeGameDetail({version_sections: updatedSections}));
        dispatch(updateSectionOrder({
            version_sections: updatedSections,
            game_id: game_id,
            version_id: current_version_id
        }));
    };

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Accordion className='section-container' alwaysOpen>
                {version_sections.map((section, index) => {
                    const sectionLanguageName = section.languages.find((lang) => lang.language_code === language)?.name;
                    const sectionLanguageHasWarnings = section.languages.find((lang) => lang.language_code === language)?.has_warnings;

                    return (
                        <Accordion.Item eventKey={section.key} key={section.key}>
                            <Accordion.Header><span
                                className='badge bg-light text-dark me-1'>#{index + 1}</span> {sectionLanguageName} {sectionLanguageHasWarnings &&
                                <ExclamationTriangleFill title={ADMIN_LANG.has_warnings}
                                                         className='text-warning ms-2'/>}</Accordion.Header>
                            <Accordion.Body>
                                <div className='d-flex flex-column'>
                                    <div className='section-details'>
                                        <div className='detail-group'>
                                            <Button
                                                onClick={() => handleEditSection(section.section_id)}>{ADMIN_LANG.section_settings}</Button>
                                            {!is_published &&
                                                <Button variant='success ms-2'
                                                        onClick={() => handleAddNewContent(section.section_id)}>
                                                    <Plus/>
                                                    <span className="ms-2">{ADMIN_LANG.new_content}</span>
                                                </Button>
                                            }
                                        </div>
                                    </div>
                                    <h3 className='pt-3 pb-3'>{ADMIN_LANG.section_contents} ({section.pages})</h3>
                                    <Droppable droppableId={section.key} key={section.key}>
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={`section-contents ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                                            >
                                                {section.contents.map((content, index) => {
                                                    return (
                                                        <Draggable isDragDisabled={!is_draft}
                                                                   key={content.key}
                                                                   draggableId={content.key}
                                                                   index={index}>
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`content-group ${snapshot.isDragging ? 'dragging' : ''}`}
                                                                >
                                                                    <ContentItem content={content} language={language} section={section}/>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>

                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>
        </DragDropContext>

    );
};

interface ContentItemProps {
    content: VersionSectionContent;
    language: string;
    section: VersionSection;
}

const ContentItem = ({content, language, section}: ContentItemProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const contentLanguageName = content.languages.find((lang: VersionSectionContentLanguage) => lang.language_code === language)?.name;
    const contentLanguageHasWarnings = content.languages.find((lang: VersionSectionContentLanguage) => lang.language_code === language)?.has_warnings;

    const handleOpenPreview = (url: string) => {
        dispatch(openPreview(url));
    }

    const handleViewStatistics = (content_id: string) => {
        navigate(`/admin/questions/${content_id}/`);
    }

    const handleEditQuestion = (content_id: string, section_id: string, type: string) => {
        dispatch(openQuestionModal({
            section_id: section_id,
            content_id: content_id,
            language: language,
            type: type
        }));
    }

    return (
        <div className='content-group-item'>
            <div className='item-group'>
                <span className='me-2'>
                    <GripVertical
                        className='utility-icon drag-icon'/>
                </span>
                <span className='badge bg-light text-dark me-2'>#{content.number}</span>{contentLanguageName}
                <span
                    className={`ms-2 badge ${content.type === 'slide' ? 'bg-secondary' : 'bg-info'}`}>
                    <span>{content.badge}</span>
                </span>
                {contentLanguageHasWarnings &&
                    <ExclamationTriangleFill title={ADMIN_LANG.has_warnings} className='text-warning ms-2'/>}
            </div>
            <div className='item-group ms-auto'>
                <Eye className='utility-icon me-1'
                     onClick={() => handleOpenPreview(content.preview_url)}/>
                {content.type === 'question' &&
                    <BarChartLine
                        onClick={() => handleViewStatistics(content.question_id)}
                        className='utility-icon me-1'/>
                }
                <Gear
                    onClick={() => handleEditQuestion(content.content_id, section.section_id, content.type)}
                    className='utility-icon'/>
            </div>
        </div>

    );
}